import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { QrCode, Fingerprint, Share2, Shield, Lock, Globe, CheckCircle2, ArrowRight } from 'lucide-react';

const DigitalCredentialsPage = () => {
  const features = [
    {
      icon: QrCode,
      title: 'QR Code Validation',
      desc: 'Every CloudXAI certificate includes a unique QR code that links directly to our verification portal. Scan it from a phone or tablet to instantly validate the credential.',
      detail: 'QR codes are embedded in digital and printed certificates. Scanning redirects to cloudxai.org/verify?cid=CERTIFICATE_ID for instant verification.',
    },
    {
      icon: Fingerprint,
      title: 'Unique Certificate ID',
      desc: 'Each credential carries a structured, tamper-evident ID in the format CXAI-[PROGRAM]-[YEAR]-[NUMBER], ensuring global uniqueness and traceability.',
      detail: 'Example: CXAI-AI-2026-0045 — where CXAI = Brand, AI = Program, 2026 = Year, 0045 = Unique sequence number.',
    },
    {
      icon: Share2,
      title: 'LinkedIn Sharing',
      desc: 'Certified professionals can share their verified credential directly to LinkedIn, adding authority to their professional profile and boosting visibility.',
      detail: 'After verification, a one-click "Share on LinkedIn" button creates a rich post with certification details and verification link.',
    },
    {
      icon: Shield,
      title: 'Security Features',
      desc: 'Our digital credentials are protected with multiple layers of security including database-backed verification, unique IDs, and status tracking.',
      detail: 'Credentials are stored in a secured database with read-only public access. Each certificate has immutable fields and real-time status tracking (Active, Processing, Revoked).',
    },
  ];

  const howItWorks = [
    { step: '01', title: 'Complete Certification', desc: 'Successfully complete a CloudXAI certification program and pass all assessments.' },
    { step: '02', title: 'Receive Digital Credential', desc: 'Get your unique Certificate ID and digital certificate with embedded QR code.' },
    { step: '03', title: 'Share & Verify', desc: 'Share on LinkedIn and professional networks. Anyone can verify via QR scan or ID lookup.' },
    { step: '04', title: 'Lifelong Validity', desc: 'Your credential is permanently verifiable online through the CloudXAI verification portal.' },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#1DE9B6] rounded-full blur-[150px]"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] mb-6 shadow-xl shadow-[#00E5FF]/50">
            <Lock className="w-10 h-10 text-[#0B0F1A]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk']" data-testid="digital-credentials-heading">
            Digital <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Credentials</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-3xl mx-auto">
            CloudXAI issues tamper-proof digital credentials verified through QR codes, unique Certificate IDs, and our secure online verification portal.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 font-['Space_Grotesk']">
              Credential <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Features</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card key={i} className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/50 transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-[#00E5FF]/30 transition-all">
                        <Icon className="w-7 h-7 text-[#0B0F1A]" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-xl mb-2 font-['Space_Grotesk'] group-hover:text-[#00E5FF] transition-colors">{f.title}</h3>
                        <p className="text-[#9CA3AF] text-sm leading-relaxed mb-3">{f.desc}</p>
                        <p className="text-[#9CA3AF]/70 text-xs leading-relaxed border-t border-[#00E5FF]/10 pt-3">{f.detail}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 font-['Space_Grotesk']">
              How It <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Works</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((h, i) => (
              <Card key={i} className="bg-[#111827] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/40 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center mx-auto mb-4">
                    <span className="text-[#0B0F1A] font-bold text-lg font-['Space_Grotesk']">{h.step}</span>
                  </div>
                  <h3 className="text-white font-bold text-base mb-2 font-['Space_Grotesk']">{h.title}</h3>
                  <p className="text-[#9CA3AF] text-sm leading-relaxed">{h.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certificate Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 font-['Space_Grotesk']">
              Sample <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Credential</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto"></div>
          </div>

          {/* Mock Certificate */}
          <div className="bg-gradient-to-br from-[#0B0F1A] to-[#111827] border-2 border-[#00E5FF]/30 rounded-2xl p-10 relative overflow-hidden" data-testid="sample-credential">
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6]"></div>
            <div className="absolute top-4 right-4 opacity-20">
              <div className="w-16 h-16 border-2 border-[#00E5FF] rounded flex items-center justify-center">
                <QrCode className="w-10 h-10 text-[#00E5FF]" />
              </div>
            </div>

            <div className="text-center space-y-4">
              <p className="text-[#00E5FF] text-xs font-bold tracking-[0.3em] uppercase">CloudXAI Technologies</p>
              <h3 className="text-3xl font-bold text-white font-['Space_Grotesk']">Certificate of Achievement</h3>
              <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent mx-auto"></div>
              <p className="text-[#9CA3AF]">This is to certify that</p>
              <p className="text-2xl font-bold text-[#00E5FF] font-['Space_Grotesk']">[Candidate Name]</p>
              <p className="text-[#9CA3AF]">has successfully completed the requirements for</p>
              <p className="text-xl font-bold text-[#1DE9B6] font-['Space_Grotesk']">[Certification Title]</p>
              <div className="flex justify-center gap-8 pt-4 text-sm">
                <div>
                  <p className="text-[#9CA3AF]">Certificate ID</p>
                  <p className="text-[#00E5FF] font-mono font-bold">CXAI-AI-2026-XXXX</p>
                </div>
                <div>
                  <p className="text-[#9CA3AF]">Issue Date</p>
                  <p className="text-white font-medium">DD/MM/YYYY</p>
                </div>
              </div>
              <div className="pt-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1DE9B6]/10 border border-[#1DE9B6]/30">
                  <CheckCircle2 className="w-4 h-4 text-[#1DE9B6]" />
                  <span className="text-[#1DE9B6] text-sm font-medium">Trained & Industry Ready</span>
                </div>
              </div>
              <p className="text-[#9CA3AF]/50 text-[10px] tracking-wider uppercase pt-4">AI . Data . Cloud</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 font-['Space_Grotesk'] text-white">
            Verify a Credential Now
          </h2>
          <p className="text-[#9CA3AF] mb-8">
            Use our verification portal to authenticate any CloudXAI certification.
          </p>
          <Link to="/verify">
            <Button className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-base rounded-lg shadow-lg shadow-[#00E5FF]/40">
              Go to Verification Portal <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default DigitalCredentialsPage;
